import { useCallback, useMemo } from "react";
import { SocialName } from "@cede/types";
import { useSocialAccounts } from "./useSocialAccounts";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useConnectStore, STEPS } from "@/store/connectStore";

// TODO: Integrate this hook into CampaignTrade.tsx (CEX trading widget) to replace
// inline social account checking logic for consistency across all trading widgets.

interface UseSocialAccountRequirementProps {
  /**
   * Array of social account names required by the campaign.
   * If empty or undefined, no social accounts are required.
   */
  requiredSocialAccounts?: SocialName[];
  /**
   * Whether the hook should be active. Useful for disabling the check
   * when not on a campaign-specific trading page.
   * @default true
   */
  enabled?: boolean;
}

interface UseSocialAccountRequirementResult {
  /**
   * True if the campaign requires any social accounts.
   */
  requiresSocialAccounts: boolean;
  /**
   * True if the user has connected at least one of the required social accounts.
   */
  hasSocialAccountsConnected: boolean;
  /**
   * True if the user is whitelisted and can bypass social requirements.
   */
  isWhitelisted: boolean;
  /**
   * True if trading should be blocked due to missing social accounts.
   * This is the main flag to use for blocking trading UI.
   * Formula: requiresSocialAccounts && !hasSocialAccountsConnected && !isWhitelisted
   */
  shouldBlockTrading: boolean;
  /**
   * True if the hook is still loading user or social account data.
   */
  isLoading: boolean;
  /**
   * Opens the social account linking modal with the LinkSocials step.
   * Call this when the user clicks on the "Connect your social accounts" button.
   */
  openSocialLinkingModal: () => void;
}

/**
 * A shared hook for checking social account requirements across trading widgets.
 *
 * This hook encapsulates the logic for:
 * - Checking if a campaign requires social accounts
 * - Checking if the user has connected the required social accounts
 * - Checking if the user is whitelisted (can bypass requirements)
 * - Opening the social account linking modal
 *
 * Use this hook in campaign-specific trading widgets to enforce social account requirements.
 * For non-campaign trading (e.g., general trade page), either pass `enabled: false` or
 * skip using this hook entirely.
 *
 * @example
 * ```tsx
 * const { shouldBlockTrading, openSocialLinkingModal } = useSocialAccountRequirement({
 *   requiredSocialAccounts: campaign.requiredSocialAccounts,
 * });
 *
 * // In button onClick:
 * if (shouldBlockTrading) {
 *   openSocialLinkingModal();
 * } else {
 *   submitTrade();
 * }
 * ```
 */
export const useSocialAccountRequirement = ({
  requiredSocialAccounts = [],
  enabled = true,
}: UseSocialAccountRequirementProps): UseSocialAccountRequirementResult => {
  const { hubUser, isLoadingHubUser } = useHubUser();
  const { hasRequiredSocialAccounts, isLoading: isLoadingSocialAccounts } = useSocialAccounts();
  const { setOpenConnectModal, setActiveStep, setSocialAccountsRequired } = useConnectStore();

  const requiresSocialAccounts = useMemo(() => {
    if (!enabled) return false;
    return requiredSocialAccounts.length > 0;
  }, [enabled, requiredSocialAccounts]);

  const hasSocialAccountsConnected = useMemo(() => {
    if (!enabled || !requiresSocialAccounts) return true;
    return hasRequiredSocialAccounts(requiredSocialAccounts);
  }, [enabled, requiresSocialAccounts, hasRequiredSocialAccounts, requiredSocialAccounts]);

  const isWhitelisted = useMemo(() => {
    return hubUser?.isWhiteListed ?? false;
  }, [hubUser?.isWhiteListed]);

  const shouldBlockTrading = useMemo(() => {
    if (!enabled) return false;
    if (!requiresSocialAccounts) return false;
    if (isWhitelisted) return false;
    return !hasSocialAccountsConnected;
  }, [enabled, requiresSocialAccounts, isWhitelisted, hasSocialAccountsConnected]);

  const isLoading = useMemo(() => {
    if (!enabled) return false;
    return isLoadingHubUser || isLoadingSocialAccounts;
  }, [enabled, isLoadingHubUser, isLoadingSocialAccounts]);

  const openSocialLinkingModal = useCallback(() => {
    setSocialAccountsRequired(requiredSocialAccounts);
    setActiveStep(STEPS.LinkSocials);
    setOpenConnectModal(true);
  }, [requiredSocialAccounts, setSocialAccountsRequired, setActiveStep, setOpenConnectModal]);

  return {
    requiresSocialAccounts,
    hasSocialAccountsConnected,
    isWhitelisted,
    shouldBlockTrading,
    isLoading,
    openSocialLinkingModal,
  };
};
